import { Link } from "react-router-dom";
import { Heart, Users, Globe, Shield, ArrowRight, ShieldCheck, CreditCard, Star, FileText, Briefcase, Award, Lightbulb } from "lucide-react";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";

const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_87d726b2-faa2-4cf3-bad6-ad391b44485d/artifacts/edr3pp66_Screenshot%202026-03-17%20at%2021.25.49.png";

export default function AboutPage() {
  const seoData = {
    title: "About Us | Paradise Borrowed",
    description: "Paradise Borrowed is a peer-to-peer lending marketplace born from one parent's frustration and a simple idea — that your neighbor's clean car seat is better than a big-box company's dirty one.",
    url: "https://www.paradiseborrowed.com/about"
  };

  return (
    <>
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonicalUrl={seoData.url}
        type="website"
        schema={generateWebPageSchema(seoData)}
      />

      <div data-testid="about-page">
        {/* Hero */}
        <section className="relative overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1769610352841-aeec32822d76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGNhcmliYmVhbiUyMGJlYWNoJTIwcGFyYWRpc2UlMjB0dXJxdW9pc2UlMjB3YXRlcnxlbnwwfHx8fDE3NzM4NDY2MDR8MA&ixlib=rb-4.1.0&q=85"
              alt="Caribbean beach"
              className="w-full h-full object-cover"
              data-testid="hero-beach-image"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/60 to-ink/30" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-36">
            <div className="max-w-xl">
              <p className="text-xs tracking-widest uppercase text-white/60 mb-4 font-ui">Our Story</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400, letterSpacing: '-0.025em' }}>
                Borrowed from<br />
                <span className="text-coral" style={{ fontStyle: 'italic' }}>paradise</span>
              </h1>
              <p className="text-base sm:text-lg text-white/80 max-w-lg leading-relaxed font-ui">
                A peer-to-peer lending marketplace born from one parent's frustration and a simple idea — that your neighbor's clean car seat is better than a big-box company's dirty one.
              </p>
            </div>
          </div>
        </section>

        {/* Origin Story */}
        <section className="py-16 sm:py-24 bg-white" data-testid="origin-story">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-3 font-ui">The origin story</p>
            <h2 className="text-2xl sm:text-3xl text-stone-900 mb-8" style={{ fontFamily: "'DM Serif Display', serif" }}>
              One dirty car seat changed everything
            </h2>
            <div className="space-y-5 text-stone-600 text-sm sm:text-base leading-relaxed font-ui">
              <p>
                For three consecutive years, our Founder made the same trip to Grand Cayman with her kid — and every year,
                she lugged along a big, bulky car seat. Anyone who's traveled with one knows the drill: it's heavy,
                and by the time it reaches its destination, it's been through the belly of a plane.
              </p>
              <p>
                On the fourth year, she decided to let it go. Booking through a car-seat rental company seemed like the obvious
                solution — $20 per day, done. Until she saw the state of the car seat.
              </p>
            </div>

            {/* Quote Block */}
            <blockquote className="my-10 pl-6 border-l-4 border-coral" data-testid="founder-quote">
              <p className="text-stone-900 text-base sm:text-lg leading-relaxed italic" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 300 }}>
                "It was dirty. And I thought — how many homes in Grand Cayman have a clean car seat just sitting in a closet,
                barely used? Probably more than five families who'd happily lend it to me for $20 a day."
              </p>
              <footer className="mt-4 text-sm text-stone-500 font-ui not-italic">
                — Fevi Yu, Founder of Paradise Borrowed
              </footer>
            </blockquote>

            <div className="space-y-5 text-stone-600 text-sm sm:text-base leading-relaxed font-ui">
              <p>
                That moment of frustration — and that flash of clarity — became the foundation of Paradise Borrowed. Why accept
                a substandard rental from a big-box company when your neighbor's gear is cleaner, better-maintained, and waiting
                to be shared?
              </p>
              <p>
                The concept is simple: people traveling to paradise destinations need things they can't easily bring. And people
                who live there already have those things — strollers, beach tents, snorkel gear, cameras, car seats — sitting
                idle between uses. Paradise Borrowed connects them.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-ink py-12 sm:py-16" data-testid="stats-bar">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <StatItem value="26" label="Launch markets across US & Caribbean" />
              <StatItem value="8" label="Premium Caribbean territories" />
              <StatItem value="23" label="Item categories to borrow" />
              <StatItem value="15+" label="Years of entrepreneurship" />
            </div>
          </div>
        </section>

        {/* Trust & Safety */}
        <section className="py-16 sm:py-24 bg-white" data-testid="trust-safety-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="mb-12">
              <p className="text-xs tracking-widest uppercase text-stone-400 mb-3 font-ui">Trust & safety</p>
              <h2 className="text-2xl sm:text-3xl text-stone-900 mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                We built trust into the platform from day one
              </h2>
              <p className="text-stone-500 text-sm sm:text-base leading-relaxed font-ui max-w-2xl">
                Paradise Borrowed isn't just a marketplace — it's a community built on accountability. Every feature, every policy,
                and every protection was designed with one goal: making it safe and simple to lend and borrow with strangers.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <TrustCard
                icon={<ShieldCheck className="w-5 h-5 text-coral" />}
                title="Email verification"
                desc="All users verify their email address before listing or borrowing. This ensures every account is tied to a real person and helps protect the community."
              />
              <TrustCard
                icon={<CreditCard className="w-5 h-5 text-coral" />}
                title="Secure payments"
                desc="All transactions are processed securely through Stripe. Lenders receive payouts after completed borrowing transactions, minus a transparent 7% platform fee."
              />
              <TrustCard
                icon={<Star className="w-5 h-5 text-coral" />}
                title="Bilateral review system"
                desc="Both lenders and borrowers leave reviews after every completed transaction, building a real reputation record over time."
              />
              <TrustCard
                icon={<FileText className="w-5 h-5 text-coral" />}
                title="Clear cancellation policies"
                desc="Transparent, tiered cancellation terms protect both sides — no surprises, no ambiguity, no disputes."
              />
            </div>
          </div>
        </section>

        {/* The Founder — Detailed Bio */}
        <section className="py-16 sm:py-24 bg-sand" data-testid="founder-bio-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-5 gap-10 items-start">
              <div className="md:col-span-2">
                <div className="sticky top-24">
                  <p className="text-xs tracking-widest uppercase text-stone-400 mb-4 font-ui">The founder</p>
                  <div className="bg-white border border-stone-200 rounded-2xl p-6 text-center">
                    <img
                      src={FOUNDER_IMAGE}
                      alt="Fevi Yu"
                      className="w-28 h-28 rounded-full object-cover mx-auto mb-4 shadow-md"
                      data-testid="founder-image"
                    />
                    <h3 className="font-medium text-stone-900 text-base font-ui">Fevi Yu</h3>
                    <p className="text-stone-500 text-xs font-ui mb-4">Founder & CEO</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      <span className="inline-flex items-center gap-1 text-[11px] font-ui text-stone-500 bg-stone-100 rounded-full px-3 py-1">
                        <Briefcase className="w-3 h-3" /> Agency Founder
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-ui text-stone-500 bg-stone-100 rounded-full px-3 py-1">
                        <Award className="w-3 h-3" /> 15+ Yrs in Business
                      </span>
                      <span className="inline-flex items-center gap-1 text-[11px] font-ui text-stone-500 bg-stone-100 rounded-full px-3 py-1">
                        <Lightbulb className="w-3 h-3" /> Creator, Basic Website Package
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-3">
                <h2 className="text-2xl sm:text-3xl text-stone-900 mb-6" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  Built by someone who's been in the trenches
                </h2>
                <div className="space-y-5 text-stone-600 text-sm sm:text-base leading-relaxed font-ui">
                  <p>
                    Fevi Yu is an entrepreneur's entrepreneur. As the founder of a digital agency and creator of
                    the Basic Website Package — a service concept that made professional web presence accessible to
                    small business owners — she's spent more than 15 years helping businesses build their digital
                    foundations from the ground up.
                  </p>
                  <p>
                    That same instinct for identifying unmet needs and building elegant solutions is what drove her
                    to create Paradise Borrowed. Fevi understands both sides of the marketplace: the traveler who
                    just wants things to work, and the owner who wants to make the most of what they have.
                  </p>
                  <p>
                    She built Paradise Borrowed to be the platform she wished had existed when she landed in Grand
                    Cayman with a car seat, a kid, and a plan to finally travel lighter.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 sm:py-24 bg-white" data-testid="mission-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <p className="text-xs tracking-widest uppercase text-stone-400 mb-3 font-ui">Our mission</p>
            <h2 className="text-2xl sm:text-3xl text-stone-900 mb-6" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Turning idle assets into shared experiences
            </h2>
            <div className="space-y-5 text-stone-600 text-sm sm:text-base leading-relaxed font-ui">
              <p>
                Every vacation destination is full of homes with closets of high-quality gear sitting unused between
                trips. Every traveler arrives wishing they didn't have to bring quite so much. Paradise Borrowed
                closes that gap.
              </p>
              <p>
                We believe local lending is the future of travel — more sustainable, more personal, and frankly, more
                fun than the corporate rental experience. When you borrow from a neighbor, you get a cleaner item, a local
                recommendation, and a story to tell.
              </p>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-16 sm:py-24 bg-sand" data-testid="values-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <p className="text-xs tracking-widest uppercase text-stone-400 mb-3 font-ui">What drives us</p>
              <h2 className="text-2xl sm:text-3xl text-stone-900" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Our values
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <ValueCard
                icon={<Heart className="w-5 h-5 text-coral" />}
                title="Community first"
                desc="Every feature we build is designed to strengthen trust between borrowers and lenders. Real reviews, email-verified profiles, and secure messaging keep our community honest."
              />
              <ValueCard
                icon={<Globe className="w-5 h-5 text-coral" />}
                title="Access over ownership"
                desc="We believe you shouldn't have to buy everything you need. Borrowing is smarter, more sustainable, and opens up experiences that ownership alone never could."
              />
              <ValueCard
                icon={<Users className="w-5 h-5 text-coral" />}
                title="Local economies matter"
                desc="Our Founder's Benefit rewards early adopters in every location. The first 10 users in each area get fee-exempt status for life — because the people who build the marketplace should benefit most."
              />
              <ValueCard
                icon={<Shield className="w-5 h-5 text-coral" />}
                title="Trust is non-negotiable"
                desc="Secure payments, email-verified accounts, logged communications, and a fair dispute resolution process. We don't cut corners on the things that matter most."
              />
            </div>
          </div>
        </section>

        {/* Founder's Benefit Highlight */}
        <section className="py-16 sm:py-24 bg-white" data-testid="founders-benefit-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="bg-stone-50 border border-stone-200 rounded-2xl p-8 sm:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-xs tracking-widest uppercase text-coral mb-3 font-ui">Early adopter reward</p>
                  <h2 className="text-2xl sm:text-3xl text-stone-900 mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
                    The Founder's Benefit
                  </h2>
                  <p className="text-stone-600 text-sm leading-relaxed font-ui mb-4">
                    The first 10 users to sign up and list items in any supported location receive a lifetime exemption
                    from our 7% platform fee on up to 25 listings. No strings attached.
                  </p>
                  <p className="text-stone-600 text-sm leading-relaxed font-ui">
                    This is our way of saying thank you to the people who help build each local marketplace from the ground up.
                    Once all 10 spots in a location are claimed, they're gone — forever.
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-stone-200 p-6 text-center">
                  <div className="text-5xl font-display text-coral mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>0%</div>
                  <p className="text-stone-900 font-medium text-sm font-ui mb-1">Platform fee for life</p>
                  <p className="text-stone-500 text-xs font-ui">Up to 25 listings per founder</p>
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <p className="text-stone-400 text-xs font-ui">Limited to 10 users per location</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24 bg-teal">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl text-white mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400, letterSpacing: '-0.025em' }}>
              Join the Borrowers Economy
            </h2>
            <p className="text-white/80 text-base max-w-md mx-auto mb-8 font-ui">
              Whether you're lending your gear or looking for something to borrow, you're in the right place.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Link
                to="/auth"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white text-teal font-medium rounded-full text-sm hover:bg-sand transition-colors font-ui"
                data-testid="about-cta-signup"
              >
                Create a free account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/browse"
                className="inline-flex items-center gap-2 px-7 py-3 bg-white/15 text-white border border-white/40 font-medium rounded-full text-sm hover:bg-white/25 transition-colors font-ui"
                data-testid="about-cta-browse"
              >
                Browse listings
              </Link>
            </div>
          </div>
        </section>

        {/* Legal Footer Note */}
        <section className="bg-white py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-stone-400 leading-relaxed font-ui text-center">
              ParadiseBorrowed.com is a Delaware corporation. Platform availability is limited to supported US states and Caribbean territories.
              For inquiries: <a href="mailto:info@paradiseborrowed.com" className="text-coral hover:underline">info@paradiseborrowed.com</a>
            </p>
          </div>
        </section>
      </div>
    </>
  );
}

function StatItem({ value, label }) {
  return (
    <div data-testid={`stat-${value.replace("+", "plus")}`}>
      <div className="text-3xl sm:text-4xl text-white mb-1" style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 400 }}>
        {value}
      </div>
      <p className="text-stone-400 text-xs sm:text-sm font-ui leading-snug">{label}</p>
    </div>
  );
}

function TrustCard({ icon, title, desc }) {
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`trust-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-10 h-10 rounded-xl bg-coral-light flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-medium text-stone-900 text-sm mb-2 font-ui">{title}</h3>
      <p className="text-stone-500 text-xs leading-relaxed font-ui">{desc}</p>
    </div>
  );
}

function ValueCard({ icon, title, desc }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 hover:shadow-md transition-shadow" data-testid={`value-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="w-10 h-10 rounded-xl bg-coral-light flex items-center justify-center mb-4">
        {icon}
      </div>
      <h3 className="font-medium text-stone-900 text-sm mb-2 font-ui">{title}</h3>
      <p className="text-stone-500 text-xs leading-relaxed font-ui">{desc}</p>
    </div>
  );
}
